import { useRouterState } from "@tanstack/react-router";

import { cn } from "@pontimed/ui";

import { DataSecurityBanner } from "./data-security-banner";

interface AccountSecurityBannerProps {
  /** Override automatic audience detection. */
  audience?: "patient" | "provider";
  /** "compact" (default) = one-line pill; "full" = collapsible card. */
  variant?: "compact" | "full";
  className?: string;
}

/**
 * Convenience wrapper around <DataSecurityBanner> for /account/* pages.
 *
 * Drop one at the bottom of every tab panel / page so the GDPR + AES-256
 * reassurance is visible everywhere. Lives INSIDE the tab's render tree
 * (NOT in AppShell) so it scrolls with the content and swipes alongside
 * the active tab — same scroll + swipe context as the rest of the panel.
 *
 * Audience auto-detected from the current route:
 *   /account/provider/* + /account/facility/*  → "provider"
 *   everything else (/account/user/*, /business/*, root) → "patient"
 *
 * Override `audience` only when the auto-detection is wrong (e.g. a
 * provider-side page that shows a patient's perspective).
 */
export function AccountSecurityBanner({
  audience,
  variant = "compact",
  className,
}: AccountSecurityBannerProps) {
  const { location } = useRouterState();
  const detected: "patient" | "provider" =
    location.pathname.startsWith("/account/provider") ||
    location.pathname.startsWith("/account/facility")
      ? "provider"
      : "patient";

  return (
    <DataSecurityBanner
      variant={variant}
      audience={audience ?? detected}
      className={cn("mt-6", className)}
    />
  );
}
